import { Component, ErrorInfo, ReactNode } from 'react';
import posthog from 'posthog-js';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
  // Name for the captured event, so a fallback can be traced back to its place.
  name?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

// Catches render errors below it and shows `fallback` instead of unmounting the
// tree to a blank page. A failed lazy chunk is the common trigger.
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(error);
    try {
      posthog.capture('error_boundary_caught', {
        boundary: this.props.name,
        message: error?.message,
        component_stack: info?.componentStack,
      });
    } catch {
      // Never let error reporting throw from inside the boundary.
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}
